import React, { useState, useRef, useEffect } from "react";
import emailjs from '@emailjs/browser';
import { motion } from 'framer-motion';
import { HiOutlineMail, HiOutlineUser, HiOutlineChatAlt2, HiOutlinePencilAlt } from 'react-icons/hi';
import { FiSend } from 'react-icons/fi';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formRef = useRef(null);

  const SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_fk5bcvy';
  const TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_9am4vps';
  const PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'YKUP2x2ETDjoRYCh5';

  useEffect(() => {
    try {
      if (PUBLIC_KEY) emailjs.init(PUBLIC_KEY);
    } catch (err) {
      console.warn('EmailJS init warning:', err);
    }
  }, [PUBLIC_KEY]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formRef.current, PUBLIC_KEY);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      setIsSubmitting(false);
      alert('Something went wrong. Please try again later.');
    }
  };

  return (
    <section id="contact" className="relative py-20 px-4 sm:px-6 lg:px-8 bg-transparent overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-80 h-80 bg-[#4779c4]/5 rounded-full blur-[120px] opacity-10"></div>
        <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-[#4779c4]/5 rounded-full blur-[120px] opacity-10"></div>
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <p className="text-slate-400 text-xs tracking-widest uppercase mb-1">Get in Touch</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white font-['Lora']">
            Let's <span className="text-[#4779c4]">talk</span>
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-[#2c456b]/35 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 sm:p-10 shadow-2xl relative overflow-hidden"
        >
          {/* Subtle Inner Glow */}
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#4779c4]/15 rounded-full blur-[80px] pointer-events-none" />
          {/* Form */}
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
            
            {/* Success Message */}
            {submitSuccess && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-3 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl text-center text-sm font-['Lora']"
              >
                Message sent successfully!
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name Field */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-300 text-xs font-medium ml-1">
                  <HiOutlineUser className="text-[#4779c4]" /> Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-5 py-3 bg-[#1b2d48]/50 border border-white/10 rounded-xl text-white text-sm focus:border-[#4779c4] focus:ring-1 focus:ring-[#4779c4] outline-none transition-all duration-300 placeholder-slate-500"
                  placeholder="Enter your name"
                  required
                />
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-slate-300 text-xs font-medium ml-1">
                  <HiOutlineMail className="text-[#4779c4]" /> Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-5 py-3 bg-[#1b2d48]/50 border border-white/10 rounded-xl text-white text-sm focus:border-[#4779c4] focus:ring-1 focus:ring-[#4779c4] outline-none transition-all duration-300 placeholder-slate-500"
                  placeholder="Enter Email address"
                  required
                />
              </div>
            </div>

            {/* Subject Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-slate-300 text-xs font-medium ml-1">
                <HiOutlineChatAlt2 className="text-[#4779c4]" /> Subject
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-5 py-3 bg-[#1b2d48]/50 border border-white/10 rounded-xl text-white text-sm focus:border-[#4779c4] focus:ring-1 focus:ring-[#4779c4] outline-none transition-all duration-300 placeholder-slate-500"
                placeholder="Enter subject"
                required
              />
            </div>

            {/* Message Field */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-slate-300 text-xs font-medium ml-1">
                <HiOutlinePencilAlt className="text-[#4779c4]" /> Message
              </label>
              <textarea
                name="message"
                rows="4"
                value={formData.message}
                onChange={handleChange}
                className="w-full px-5 py-3 bg-[#1b2d48]/50 border border-white/10 rounded-xl text-white text-sm focus:border-[#4779c4] focus:ring-1 focus:ring-[#4779c4] outline-none transition-all duration-300 resize-none placeholder-slate-500"
                placeholder="Enter your message"
                required
              ></textarea>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-[#4779c4] hover:bg-[#3c649f] text-white font-bold rounded-xl transition-all duration-300 shadow-xl shadow-[#4779c4]/20 flex items-center justify-center gap-3 group disabled:opacity-70 disabled:cursor-not-allowed text-sm"
            >
              {isSubmitting ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Send Message</span>
                  <FiSend className="text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}